'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Link from 'next/link';
import api from '@/lib/api';
import { Pipeline, PipelineParam, PipelineBatch, Series, PermutationList } from '@/lib/types';
import StatusBar from '@/components/StatusBar';
import { useConfirm } from '@/components/ConfirmDialog';

const TERMINAL = ['completed', 'failed', 'cancelled'];
const MAX_ROWS = 500;

/** Where a param's column of values comes from. */
type Source =
  | { kind: 'none' }
  | { kind: 'text'; text: string }
  | { kind: 'list'; listId: number | null };

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued: 'text-[var(--text-muted)] bg-[var(--bg-elevated)]',
    processing: 'text-[var(--info)] bg-[var(--info-soft)]',
    completed: 'text-[var(--success)] bg-[var(--accent-soft)]',
    failed: 'text-[var(--error)] bg-[var(--error-soft)]',
    cancelled: 'text-[var(--warning)] bg-[var(--warning-soft)]',
    pending: 'text-[var(--text-muted)] bg-[var(--bg-elevated)]',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${map[status] ?? map.queued}`}>
      {status}
    </span>
  );
}

const trunc = (s: string, n = 60) => (s.length > n ? s.slice(0, n) + '…' : s);

export default function BatchPage() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [series, setSeries] = useState<Series[]>([]);
  const [permLists, setPermLists] = useState<PermutationList[]>([]);
  const [pipelineId, setPipelineId] = useState<number | null>(null);
  const [params, setParams] = useState<PipelineParam[] | null>(null);
  const [sources, setSources] = useState<Record<string, Source>>({});
  const [combine, setCombine] = useState<'product' | 'zip'>('product');
  const [seriesId, setSeriesId] = useState<number | null>(null);
  const [batch, setBatch] = useState<PipelineBatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { confirm } = useConfirm();

  // Load pipelines + series + permutation lists once.
  useEffect(() => {
    api.listPipelines().then(setPipelines).catch(() => {});
    api.getSeries().then(setSeries).catch(() => {});
    api.getPermutationLists().then(setPermLists).catch(() => {});
  }, []);

  // Preselect a pipeline from the ?pipeline=<id> deep-link (e.g. the editor's "Run batch").
  // Read from window.location to avoid the useSearchParams Suspense de-opt.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('pipeline');
    const n = Number(q);
    if (q && Number.isFinite(n)) setPipelineId(n);
  }, []);

  // Discover params when a pipeline is picked; seed the first text/number input with a paste box.
  useEffect(() => {
    if (pipelineId == null) {
      setParams(null);
      return;
    }
    setParams(null);
    api
      .getPipelineParams(pipelineId)
      .then((r) => {
        setParams(r.params);
        const usable = r.params.filter((p) => p.type === 'string' || p.type === 'number');
        const init: Record<string, Source> = {};
        usable.forEach((p, i) => (init[p.name] = i === 0 ? { kind: 'text', text: '' } : { kind: 'none' }));
        setSources(init);
      })
      .catch(() => setParams([]));
  }, [pipelineId]);

  // A list can fill any text or number input (image inputs can't be batched from a list).
  const usableParams = useMemo(
    () => (params ?? []).filter((p) => p.type === 'string' || p.type === 'number'),
    [params],
  );

  const setSource = useCallback(
    (name: string, s: Source) => setSources((prev) => ({ ...prev, [name]: s })),
    [],
  );

  // Resolve a param's source into its list of values.
  const valuesFor = useCallback(
    (name: string): string[] => {
      const s = sources[name];
      if (!s || s.kind === 'none') return [];
      if (s.kind === 'text') return s.text.split('\n').map((t) => t.trim()).filter(Boolean);
      const list = permLists.find((l) => l.id === s.listId);
      return (list?.items ?? []).filter((it) => it.is_active).map((it) => it.value);
    },
    [sources, permLists],
  );

  // Active columns = usable params that currently resolve to >=1 value.
  const cols = useMemo(
    () => usableParams.map((p) => ({ param: p.name, values: valuesFor(p.name) })).filter((c) => c.values.length > 0),
    [usableParams, valuesFor],
  );

  const rowCount = useMemo(() => {
    if (!cols.length) return 0;
    return combine === 'zip'
      ? Math.min(...cols.map((c) => c.values.length))
      : cols.reduce((a, c) => a * c.values.length, 1);
  }, [cols, combine]);

  const buildRows = useCallback(() => {
    if (!cols.length) return [];
    if (combine === 'zip') {
      const n = Math.min(...cols.map((c) => c.values.length));
      return Array.from({ length: n }, (_, i) => ({
        name: trunc(cols.map((c) => c.values[i]).join(' · ')),
        inputs: Object.fromEntries(cols.map((c) => [c.param, c.values[i]])),
      }));
    }
    let combos: string[][] = [[]];
    for (const c of cols) combos = combos.flatMap((combo) => c.values.map((v) => [...combo, v]));
    return combos.map((combo) => ({
      name: trunc(combo.join(' · ')),
      inputs: Object.fromEntries(cols.map((c, i) => [c.param, combo[i]])),
    }));
  }, [cols, combine]);

  const canRun = pipelineId != null && rowCount > 0 && rowCount <= MAX_ROWS && !busy;

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(
    (id: number) => {
      stopPolling();
      const tick = async () => {
        try {
          const b = await api.getPipelineBatch(id);
          setBatch(b);
          if (TERMINAL.includes(b.status)) stopPolling();
        } catch {
          /* keep trying */
        }
      };
      tick();
      pollRef.current = setInterval(tick, 1000);
    },
    [stopPolling],
  );

  useEffect(() => stopPolling, [stopPolling]);

  const run = useCallback(async () => {
    if (!canRun || pipelineId == null) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createPipelineBatch(pipelineId, {
        name: pipelines.find((p) => p.id === pipelineId)?.name ?? null,
        rows: buildRows(),
        options: seriesId != null ? { series_id: seriesId } : {},
      });
      setBatch(created);
      poll(created.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [canRun, pipelineId, buildRows, seriesId, pipelines, poll]);

  const cancel = useCallback(async () => {
    if (!batch) return;
    try {
      await api.cancelPipelineBatch(batch.id);
    } catch {
      /* ignore */
    }
  }, [batch]);

  const downloadZip = useCallback(async () => {
    if (!batch) return;
    try {
      const blob = await api.downloadBatchZip(batch.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batch.name || `batch-${batch.id}`}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [batch]);

  const deleteBatch = useCallback(async () => {
    if (!batch) return;
    const ok = await confirm({
      title: 'Delete batch?',
      message: 'This removes the batch, its runs, and any saved outputs. This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      stopPolling();
      await api.deletePipelineBatch(batch.id);
      setBatch(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [batch, confirm, stopPolling]);

  const done = (batch?.completed ?? 0) + (batch?.failed ?? 0);
  const pct = batch && batch.total > 0 ? Math.round((done / batch.total) * 100) : 0;
  const running = batch != null && !TERMINAL.includes(batch.status);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={null} stats={null} onRefresh={() => batch && poll(batch.id)} />

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-semibold tracking-tight">Batch</h1>
              <Link href="/pipeline" className="text-sm text-[var(--accent)] hover:underline">
                ← Pipeline editor
              </Link>
            </div>
            <p className="text-[var(--text-muted)]">
              Run a saved pipeline once per input — from a pasted list or your permutation lists.
            </p>
          </div>

          {/* Setup */}
          <div className="card-elevated p-5 space-y-4">
            {/* Pipeline picker */}
            <label className="block">
              <span className="text-sm text-[var(--text-secondary)]">Pipeline</span>
              <select
                data-testid="pipeline-select"
                className="input w-full mt-1"
                value={pipelineId ?? ''}
                onChange={(e) => {
                  setPipelineId(e.target.value ? Number(e.target.value) : null);
                  setBatch(null);
                }}
              >
                <option value="">Select a saved pipeline…</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            {pipelineId != null && params != null && usableParams.length === 0 && (
              <p className="text-sm text-[var(--warning)]">
                This pipeline has no text or number inputs. Add a Prompt with a <code>{'{token}'}</code>,
                or set a param name on a Text/Number node, to batch over it.
              </p>
            )}

            {usableParams.length > 0 && (
              <>
                {/* Per-param source builder */}
                <div className="space-y-3">
                  {usableParams.map((p) => {
                    const s = sources[p.name] ?? { kind: 'none' as const };
                    const n = valuesFor(p.name).length;
                    return (
                      <div key={p.name} className="rounded-lg border border-[var(--border-subtle)] p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium">
                            {p.name} <span className="text-xs text-[var(--text-muted)]">({p.type})</span>
                          </span>
                          <div className="flex items-center gap-2">
                            {n > 0 && <span className="text-xs text-[var(--text-muted)]">{n} values</span>}
                            <select
                              data-testid={`src-${p.name}`}
                              className="input text-xs py-1"
                              value={s.kind}
                              onChange={(e) => {
                                const k = e.target.value as Source['kind'];
                                if (k === 'text') setSource(p.name, { kind: 'text', text: '' });
                                else if (k === 'list')
                                  setSource(p.name, { kind: 'list', listId: permLists[0]?.id ?? null });
                                else setSource(p.name, { kind: 'none' });
                              }}
                            >
                              <option value="none">— skip (use default) —</option>
                              <option value="text">Paste a list</option>
                              <option value="list" disabled={permLists.length === 0}>
                                Permutation list
                              </option>
                            </select>
                          </div>
                        </div>

                        {s.kind === 'text' && (
                          <textarea
                            data-testid={`paste-${p.name}`}
                            className="input w-full font-mono text-sm"
                            rows={5}
                            placeholder={'a red goblin\na purple imp\na green ogre'}
                            value={s.text}
                            onChange={(e) => setSource(p.name, { kind: 'text', text: e.target.value })}
                          />
                        )}

                        {s.kind === 'list' && (
                          <select
                            data-testid={`list-${p.name}`}
                            className="input w-full text-sm"
                            value={s.listId ?? ''}
                            onChange={(e) =>
                              setSource(p.name, { kind: 'list', listId: e.target.value ? Number(e.target.value) : null })
                            }
                          >
                            <option value="">Select a list…</option>
                            {permLists.map((l) => (
                              <option key={l.id} value={l.id}>
                                {l.name} ({l.active_count})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Combine mode — only matters with 2+ active columns */}
                {cols.length > 1 && (
                  <label className="flex items-center gap-3 text-sm">
                    <span className="text-[var(--text-secondary)]">Combine</span>
                    <select
                      data-testid="combine"
                      className="input text-sm py-1"
                      value={combine}
                      onChange={(e) => setCombine(e.target.value as 'product' | 'zip')}
                    >
                      <option value="product">Product (every combination)</option>
                      <option value="zip">Zip (row by row)</option>
                    </select>
                  </label>
                )}

                {/* Series target */}
                <label className="block">
                  <span className="text-sm text-[var(--text-secondary)]">Save outputs to series (optional)</span>
                  <select
                    className="input w-full mt-1"
                    value={seriesId ?? ''}
                    onChange={(e) => setSeriesId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">— none (uses the pipeline&apos;s own save nodes) —</option>
                    {series.map((sx) => (
                      <option key={sx.id} value={sx.id}>
                        {sx.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex items-center gap-3 pt-1">
                  <button className="btn btn-primary" disabled={!canRun} onClick={run}>
                    {busy ? 'Starting…' : `Run batch (${rowCount})`}
                  </button>
                  <span className="text-sm text-[var(--text-muted)]">
                    {rowCount} run{rowCount === 1 ? '' : 's'}
                  </span>
                  {rowCount > MAX_ROWS && (
                    <span className="text-sm text-[var(--error)]">over the {MAX_ROWS}-run limit — narrow it down</span>
                  )}
                </div>
              </>
            )}

            {error && <p className="text-sm text-[var(--error)]">{error}</p>}
          </div>

          {/* Progress */}
          {batch && (
            <div className="card-elevated p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h2 className="text-lg font-semibold">{batch.name || `Batch #${batch.id}`}</h2>
                  <StatusBadge status={batch.status} />
                </div>
                <div className="flex items-center gap-2">
                  {(batch.completed ?? 0) > 0 && (
                    <button className="btn btn-ghost text-sm" onClick={downloadZip} title="Download all outputs, named by row">
                      Download .zip
                    </button>
                  )}
                  {running ? (
                    <button className="btn btn-ghost text-sm" onClick={cancel}>
                      Cancel
                    </button>
                  ) : (
                    <button
                      className="btn btn-ghost text-sm text-[var(--error)]"
                      onClick={deleteBatch}
                      title="Delete this batch, its runs, and saved outputs"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-1">
                <div className="h-2 rounded-full bg-[var(--bg-elevated)] overflow-hidden">
                  <div className="h-full bg-[var(--accent)] transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex justify-between text-xs text-[var(--text-muted)]">
                  <span>
                    {done}/{batch.total} done
                    {batch.failed > 0 && <span className="text-[var(--error)]"> · {batch.failed} failed</span>}
                  </span>
                  <span>{pct}%</span>
                </div>
              </div>

              {/* Per-row table */}
              <div className="divide-y divide-[var(--border-subtle)]">
                {(batch.row_results ?? []).map((r) => {
                  const img = r.outputs?.find((o) => o.image)?.image;
                  return (
                    <div key={r.row} className="flex items-center gap-3 py-2">
                      <div className="w-10 h-10 rounded-lg bg-[var(--bg-elevated)] flex-shrink-0 overflow-hidden">
                        {img && (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img src={api.getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <span className="text-sm flex-1 min-w-0 truncate" title={r.name ?? ''}>
                        {r.name || `row ${r.row + 1}`}
                      </span>
                      {r.error && (
                        <span className="text-xs text-[var(--error)] truncate max-w-[40%]" title={r.error}>
                          {r.error}
                        </span>
                      )}
                      <StatusBadge status={r.status} />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
